// Fills the actual IRS Form W-9 PDF (placed at /public/forms/fw9.pdf) using
// pdf-lib's AcroForm API. Field names in IRS PDFs are versioned and
// inconsistent (e.g. `topmostSubform[0].Page1[0].f1_01[0]`), so we look up
// each value by trying a list of plausible field-name patterns. The first
// match wins; misses are silently skipped so a slightly-different PDF still
// works for what it can.

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

const W9_URL = '/forms/fw9.pdf';

// Map of logical W-9 field → array of candidate AcroForm names to try.
// Patterns cover the IRS PDFs released 2018, 2024, and the official-but-renamed
// fillable versions that downstream tools (Adobe, browser viewers) sometimes save.
const FIELD_NAMES = {
  legal_name:           ['topmostSubform[0].Page1[0].f1_01[0]', 'f1_1[0]', 'f1_01[0]', 'Name'],
  business_name:        ['topmostSubform[0].Page1[0].f1_02[0]', 'f1_2[0]', 'f1_02[0]', 'BusinessName'],
  llc_classification:   ['topmostSubform[0].Page1[0].f1_03[0]', 'f1_3[0]', 'f1_03[0]'],
  other_classification: ['topmostSubform[0].Page1[0].f1_04[0]', 'f1_4[0]', 'f1_04[0]'],
  exempt_payee_code:    ['topmostSubform[0].Page1[0].f1_05[0]', 'f1_5[0]', 'f1_05[0]'],
  fatca_code:           ['topmostSubform[0].Page1[0].f1_06[0]', 'f1_6[0]', 'f1_06[0]'],
  address:              ['topmostSubform[0].Page1[0].f1_07[0]', 'f1_7[0]', 'f1_07[0]', 'Address'],
  city_state_zip:       ['topmostSubform[0].Page1[0].f1_08[0]', 'f1_8[0]', 'f1_08[0]'],
  account_numbers:      ['topmostSubform[0].Page1[0].f1_09[0]', 'f1_9[0]', 'f1_09[0]'],
  requester_name:       ['topmostSubform[0].Page1[0].f1_10[0]', 'f1_10[0]'],
  // SSN as three boxes (some PDFs) or one (others).
  ssn1: ['topmostSubform[0].Page1[0].f1_11[0]', 'f1_11[0]'],
  ssn2: ['topmostSubform[0].Page1[0].f1_12[0]', 'f1_12[0]'],
  ssn3: ['topmostSubform[0].Page1[0].f1_13[0]', 'f1_13[0]'],
  ssn_full: ['SSN', 'TIN'],
  // EIN as two boxes (some PDFs) or one (others).
  ein1: ['topmostSubform[0].Page1[0].f1_14[0]', 'f1_14[0]'],
  ein2: ['topmostSubform[0].Page1[0].f1_15[0]', 'f1_15[0]'],
  ein_full: ['EIN'],
};

// Tax classification checkbox names. Indices vary by PDF revision.
const CHECKBOX_NAMES = {
  individual:    ['topmostSubform[0].Page1[0].c1_1[0]', 'c1_1[0]'],
  c_corp:        ['topmostSubform[0].Page1[0].c1_1[1]', 'c1_1[1]'],
  s_corp:        ['topmostSubform[0].Page1[0].c1_1[2]', 'c1_1[2]'],
  partnership:   ['topmostSubform[0].Page1[0].c1_1[3]', 'c1_1[3]'],
  trust_estate:  ['topmostSubform[0].Page1[0].c1_1[4]', 'c1_1[4]'],
  llc:           ['topmostSubform[0].Page1[0].c1_1[5]', 'c1_1[5]'],
  other:         ['topmostSubform[0].Page1[0].c1_1[6]', 'c1_1[6]'],
};

function tryGetField(form, names, kind) {
  for (const n of names) {
    try {
      if (kind === 'text') return form.getTextField(n);
      if (kind === 'checkbox') return form.getCheckBox(n);
    } catch (_) { /* try next */ }
  }
  return null;
}

function setText(form, names, value) {
  if (value == null || value === '') return;
  const f = tryGetField(form, names, 'text');
  if (f) {
    try { f.setText(String(value)); } catch (_) { /* read-only or wrong type */ }
  }
}

function setCheck(form, names, on) {
  const f = tryGetField(form, names, 'checkbox');
  if (!f) return;
  try {
    if (on) f.check(); else f.uncheck();
  } catch (_) { /* ignore */ }
}

// Pads/breaks an SSN/EIN into per-box pieces if the PDF uses separate boxes.
function splitDigits(digits, groups) {
  const clean = (digits || '').replace(/\D/g, '');
  const out = [];
  let i = 0;
  for (const g of groups) {
    out.push(clean.slice(i, i + g));
    i += g;
  }
  return out;
}

/**
 * @param {object} v   W-9 form values from W9Form state
 * @param {Uint8Array=} signatureBytes  PNG bytes of the signature image (optional)
 * @returns {Promise<Uint8Array>}  Filled (and optionally flattened) PDF bytes
 */
export async function fillW9Pdf(v, signatureBytes = null, { flatten = false } = {}) {
  const baseBytes = await fetch(W9_URL).then((r) => {
    if (!r.ok) throw new Error(`Couldn't load IRS W-9 PDF (${r.status}). Make sure /public/forms/fw9.pdf exists.`);
    return r.arrayBuffer();
  });
  const pdf = await PDFDocument.load(baseBytes);
  const form = pdf.getForm();

  // Text fields
  setText(form, FIELD_NAMES.legal_name,           v.legal_name);
  setText(form, FIELD_NAMES.business_name,        v.business_name);
  setText(form, FIELD_NAMES.llc_classification,   v.llc_classification);
  setText(form, FIELD_NAMES.other_classification, v.other_classification);
  setText(form, FIELD_NAMES.exempt_payee_code,    v.exempt_payee_code);
  setText(form, FIELD_NAMES.fatca_code,           v.fatca_code);
  setText(form, FIELD_NAMES.address,              v.address + (v.address2 ? `, ${v.address2}` : ''));
  setText(form, FIELD_NAMES.city_state_zip,       [v.city, v.state, v.zip].filter(Boolean).join(', '));
  setText(form, FIELD_NAMES.account_numbers,      v.account_numbers);

  // SSN: try full-field first, then 3 boxes
  if ((v.tin_type || 'ssn') === 'ssn' && v.tin) {
    setText(form, FIELD_NAMES.ssn_full, v.tin);
    const [a, b, c] = splitDigits(v.tin, [3, 2, 4]);
    setText(form, FIELD_NAMES.ssn1, a);
    setText(form, FIELD_NAMES.ssn2, b);
    setText(form, FIELD_NAMES.ssn3, c);
  }
  // EIN
  if (v.tin_type === 'ein' && v.tin) {
    setText(form, FIELD_NAMES.ein_full, v.tin);
    const [a, b] = splitDigits(v.tin, [2, 7]);
    setText(form, FIELD_NAMES.ein1, a);
    setText(form, FIELD_NAMES.ein2, b);
  }

  // Tax classification checkbox — pick the right one
  const cls = v.tax_classification;
  for (const [key, names] of Object.entries(CHECKBOX_NAMES)) {
    setCheck(form, names, key === cls);
  }

  // Stamp signature + date onto page 1 below Part II.
  // Coordinates are tuned for the IRS Rev. March 2024 PDF (612 x 792 pts).
  // They're stamped via drawing (not an AcroForm field) so they survive flattening.
  const pages = pdf.getPages();
  const page1 = pages[0];
  const { width, height } = page1.getSize();

  if (signatureBytes) {
    try {
      const png = await pdf.embedPng(signatureBytes);
      // Sign-here box on IRS W-9 (approx). Resize to box height.
      const sigBoxW = 220, sigBoxH = 26;
      const sigX = 110;
      const sigY = height - 562; // ~230 from top? tuned visually for Mar 2024 PDF
      const dims = png.scale(Math.min(sigBoxW / png.width, sigBoxH / png.height));
      page1.drawImage(png, { x: sigX, y: sigY, width: dims.width, height: dims.height });
    } catch (_) { /* ignore embed errors */ }
  }

  if (v.signed_date) {
    try {
      const font = await pdf.embedFont(StandardFonts.Helvetica);
      page1.drawText(v.signed_date, {
        x: 420,
        y: height - 558,
        size: 10,
        font,
        color: rgb(0, 0, 0),
      });
    } catch (_) { /* ignore */ }
  }

  if (flatten) form.flatten();
  return pdf.save();
}

// Convenience: returns a Blob URL the caller can stuff into an <iframe src>.
export async function buildW9BlobUrl(values, signatureBytes = null) {
  const bytes = await fillW9Pdf(values, signatureBytes, { flatten: false });
  const blob = new Blob([bytes], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

// Helper for debugging field names against whatever PDF the user drops in.
export async function logW9FieldNames() {
  const baseBytes = await fetch(W9_URL).then((r) => r.arrayBuffer());
  const pdf = await PDFDocument.load(baseBytes);
  const fields = pdf.getForm().getFields();
  // eslint-disable-next-line no-console
  console.table(fields.map((f) => ({ name: f.getName(), type: f.constructor.name })));
}
